import React, { useRef, useState } from 'react';
import { X, ImagePlus, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../hooks/use-toast';
import { api, imgUrl, apiError } from '../../lib/api';

export const PhotoUploader = ({ photos, setPhotos }) => {
  const { t } = useApp();
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8 - photos.length);
    e.target.value = '';
    if (!files.length) return;
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    setUploading(true);
    try {
      const { data } = await api.post('/uploads', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotos((p) => [...p, ...data.urls]);
    } catch (err) {
      console.error('Upload failed', err);
      toast({ title: t('post.uploadError'), description: apiError(err) });
    } finally {
      setUploading(false);
    }
  };
  const remove = (url) => setPhotos((arr) => arr.filter((p) => p !== url));

  return (
    <div>
      <label className="block text-sm font-bold text-[#1A1A1A] mb-2">{t('post.photos')}</label>
      <p className="text-xs text-[#7A7468] mb-3">{t('post.photos.hint')} · {t('post.uploadHint')}</p>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={onFiles} data-testid="photo-file-input" />
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="photo-grid">
        {photos.map((url, i) => (
          <div key={url} className="relative aspect-square rounded-xl overflow-hidden bg-[#FAF7F2] border border-[#EFEAE0] group" data-testid={`photo-item-${i}`}>
            <img src={imgUrl(url)} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-[#0F8B5C] text-white text-[10px] font-bold">{t('post.cover')}</span>}
            <button type="button" onClick={() => remove(url)} data-testid={`remove-photo-${i}`} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
          </div>
        ))}
        {photos.length < 8 && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="add-photo-button" className="aspect-square rounded-xl border-2 border-dashed border-[#EFEAE0] hover:border-[#0F8B5C] hover:bg-[#0F8B5C]/5 flex flex-col items-center justify-center gap-1 text-[#7A7468] hover:text-[#0F8B5C] transition-colors">
            {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
            <span className="text-xs font-medium">{uploading ? t('post.uploading') : t('post.add')}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export const ToggleOption = ({ icon: Icon, title, hint, checked, onChange, accent = false, testId }) => (
  <div className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${accent && checked ? 'bg-[#F2B441]/10 border-[#F2B441]' : 'bg-[#FAF7F2] border-[#EFEAE0]'}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent ? 'bg-[#F2B441]/20' : 'bg-[#0F8B5C]/10'}`}><Icon className={`w-5 h-5 ${accent ? 'text-[#8B6914]' : 'text-[#0F8B5C]'}`} /></div>
      <div>
        <div className="font-semibold text-sm text-[#1A1A1A]">{title}</div>
        <div className="text-xs text-[#7A7468]">{hint}</div>
      </div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);
